import isEmpty from 'lodash/isEmpty';
import flow from 'lodash/fp/flow';
import filter from 'lodash/fp/filter';
import sortBy from 'lodash/fp/sortBy';
import { createSelector } from 'reselect';
import { UserState } from 'state/modules/user';
import { WorkbookState } from 'types';

const getUsersWorkbooks = (workbook: WorkbookState) => workbook;
const getUsersLicenses = (workbook: WorkbookState, user: UserState) => user.licenses;
const getUsersClickAways = (workbook: WorkbookState, user: UserState) => user.clickAways;

const isExpired = workbook => (licenses, clickAways) =>
  !isEmpty(filter(expired(workbook))(licenses)) && !isEmpty(filter(expired(workbook))(clickAways));

const expired = workbook => license =>
  workbook.products
    .map(product => product.productCurriculumId)
    .includes(license.productCurriculumId) && license.expired;

const limitedAccessWorkbook =
  workbook =>
  (licenses, clickAways = [] as any[]) =>
    !isEmpty(filter(isTrialLicense(workbook))(licenses)) && !isClickAway(workbook, clickAways);

const isTrialLicense = workbook => license =>
  workbook.products
    .map(product => product.productCurriculumId)
    .includes(license.productCurriculumId) && license.trial;

const isClickAway = (workbook, clickAways = [] as any[]) =>
  !!workbook.products.find(x =>
    clickAways.find((y: any) => (y.productCurriculumId = x.productCurriculumId))
  );

export const getCurrentPage = (state: WorkbookState, routeParams: any) => {
  const currentWorkbook = getCurrentWorkbook(state, routeParams);

  const pageNumber = parseInt(routeParams.pageNumber, 10);

  return pageNumber > currentWorkbook.totalPages ? currentWorkbook.totalPages : pageNumber;
};

/* 
  TODO should remove since currentWorkbook is in state -> state.app.currentWorkbook
*/
export const getCurrentWorkbook = createSelector(
  [(_, params) => params, state => state],
  (bookParams, state) => {
    const { bookCode, edition } = bookParams;
    const currentWorkbook = (state as any).entity.workbooks.find(
      book => book.bookCode === bookCode && book.edition.toString() === edition
    );
    // pdf.numPages includes the intro page (page i), so page 1 is page 2 in the pdf.

    const { workbookContent: content, pageDetails } = (state as any).entity;
    return {
      ...currentWorkbook,
      content,
      pageDetails
    };
  }
);

export const getCurrentStudentWorkbook = createSelector(
  getCurrentWorkbook,
  getUsersWorkbooks,
  state => state,
  (currentWorkbook, usersWorkbooks, workbookState) => {
    const currentWorkbook1 = currentWorkbook as any;
    const usersWorkbooks1 = usersWorkbooks as any;

    if (
      currentWorkbook1 &&
      currentWorkbook1.workbookId &&
      usersWorkbooks1[currentWorkbook1.workbookId]
    ) {
      return {
        ...(workbookState as any),
        ...usersWorkbooks1[currentWorkbook1.workbookId]
      };
    }

    return currentWorkbook1;
  }
);

export const getUsersStudentWorkbooks = createSelector(
  getUsersWorkbooks,
  getUsersLicenses,
  getUsersClickAways,
  (workbooks, usersLicenses, usersClickAways) =>
    flow(
      filter((book: any) => book.type === 'workbook'),
      filter((book: any) => !isExpired(book)(usersLicenses, usersClickAways)),
      sortBy('gradeOrder')
    )(workbooks)
);

export const getUsersInactiveWorkbooks = createSelector(
  (workbookState, userState, routeParams) => getCurrentStudentWorkbook(workbookState, routeParams),
  getUsersWorkbooks,
  getUsersLicenses,
  getUsersClickAways,
  (activeWorkbook, workbooks, usersLicenses, usersClickAways) =>
    flow(
      filter((book: any) => book.bookCode !== (activeWorkbook as any).bookCode),
      filter((book: any) => book.type === 'workbook'),
      filter((book: any) => !isExpired(book)(usersLicenses, usersClickAways)),
      sortBy('gradeOrder')
    )(workbooks)
);

export const getLimitedLicensedWorkbooks = createSelector(
  getUsersWorkbooks,
  getUsersLicenses,
  getUsersClickAways,
  (workbooks, usersLicenses, usersClickAways) =>
    flow(
      filter((book: any) => book.type === 'workbook'),
      filter((book: any) => !isExpired(book)(usersLicenses, usersClickAways)),
      filter((book: any) => limitedAccessWorkbook(book)(usersLicenses, usersClickAways)),
      sortBy('gradeOrder')
    )(workbooks)
);

export const getFullLicensedWorkbooks = createSelector(
  getUsersWorkbooks,
  getUsersLicenses,
  getUsersClickAways,
  (workbooks, usersLicenses, usersClickAways) =>
    flow(
      filter((book: any) => book.type === 'workbook'),
      filter(book => !isExpired(book)(usersLicenses, usersClickAways)),
      filter(book => !limitedAccessWorkbook(book)(usersLicenses, usersClickAways)),
      sortBy('gradeOrder')
    )(workbooks)
);
